import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { UseComboboxPropGetters } from 'downshift';
import HtmlToReact from 'html-to-react';
import React from 'react';
import {
  RecentSearchRemoveButton,
  SuggestionItemWrapper
} from './SearchBar.styles';
import { SearchType } from '../constants';
import type { Suggestion, SuggestionItem } from '../contracts';
import Clock from '../icons/Clock';
import CloseIcon from '../icons/Close';
import Heart from '../icons/Heart';
import SearchIcon from '../icons/Search';

const htmlParser = HtmlToReact.Parser();

const SuggestionIcon = ({ type }: { type: SearchType }) => {
  switch (type) {
    case SearchType.SavedSearch:
      return <Heart />;
    case SearchType.RecentSearch:
      return <Clock />;
    case SearchType.TextSearch:
      return <SearchIcon />;
    default:
      return null;
  }
};

type OwnProps = {
  className?: string;
  getItemProps: UseComboboxPropGetters<Suggestion>['getItemProps'];
  handleRemoveRecentSearch: React.MouseEventHandler<HTMLButtonElement>;
  handleSuggestionClick: React.MouseEventHandler<HTMLButtonElement>;
  hasInput: boolean;
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  suggestionResults: SuggestionItem[];
};

export default function Suggestions({
  className,
  getItemProps,
  handleRemoveRecentSearch,
  handleSuggestionClick,
  hasInput,
  highlightedIndex,
  setHighlightedIndex,
  suggestionResults
}: OwnProps) {
  return suggestionResults.length ? (
    <div
      className={cx('flex flex-col gap-8 bg-white', className)}
      onMouseLeave={() => setHighlightedIndex(hasInput ? 0 : -1)}
    >
      {suggestionResults.map((item, index) => (
        <div className="flex flex-col gap-8" key={`${item.text}${index}`}>
          <div
            {...getItemProps({
              item,
              index
            })}
          >
            <Clickable
              className="flex cursor-pointer items-center justify-between rounded-[3px] transition-all duration-300"
              data-analytics-id="Search Bar - Suggestion"
              data-qa="Search Bar - Suggestion"
              onClick={handleSuggestionClick}
              onMouseEnter={() => setHighlightedIndex(index)}
              onTouchStart={() => setHighlightedIndex(index)}
              style={{
                backgroundColor:
                  highlightedIndex === index
                    ? 'var(--cvna-color-grey-1)'
                    : 'var(--cvna-color-white)'
              }}
            >
              <SuggestionItemWrapper title={item.text}>
                <div className="flex w-16 items-center">
                  <SuggestionIcon type={item.searchType} />
                </div>
                <span className="overflow-hidden text-ellipsis">
                  {htmlParser.parse(item.highlightedText)}
                </span>
              </SuggestionItemWrapper>
              {item.searchType === SearchType.RecentSearch && (
                <RecentSearchRemoveButton
                  type="button"
                  onClick={handleRemoveRecentSearch}
                  data-analytics-id="Search Bar - Remove Recent Search"
                  data-qa="Search Bar - Remove Recent Search"
                  value={item.text}
                >
                  <CloseIcon />
                </RecentSearchRemoveButton>
              )}
            </Clickable>
          </div>
          {hasInput && index === suggestionResults.length - 1 ? null : (
            <div className="w-full border-b border-grey-2" />
          )}
        </div>
      ))}
    </div>
  ) : null;
}
